import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { AutocompleteOptionType } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { AssignmentDto, AssignmentGetPaginatedDto } from "@/core/api/generated/v1";

import VehicleInline from "../Vehicle/VehicleInline";
import { assignmentToOption } from "./AssignmentAutocomplete";

export interface AssignmentsAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<AssignmentDto> {
  searchFilters?: {
    vehicleId?: string;
  } & Partial<Omit<AssignmentGetPaginatedDto, "search" | "includeIds">>;
}

export default function AssignmentsAutocomplete({
  isPreload = true,
  searchFilters,
  ...otherProps
}: AssignmentsAutocompleteProps) {
  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={assignmentToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.assignmentsApi.apiV1AssignmentsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          assignmentGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          assignmentGetPaginatedDto: {
            ...params.assignmentGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Assignments'
      placeholder='Search for assignments...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='assignment' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography component='div' variant='body1'>
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2' color='secondary'>
                      <Box>
                        <VehicleInline
                          entity={option.data.vehicle}
                          withIcon={false}
                          withTooltip={false}
                        />
                      </Box>
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
